import { inRange } from 'lodash';
import {
  BillingCycle,
  BillingVersion,
  ChangePlanEstimateResponse,
  Invoice,
  InvoiceDetails,
  MonthlyPrice,
  Plan,
  PlanHierarchy,
  PlanIds,
  PlanNames,
  Plans,
  RestrictedPlans,
  SupportedPlans,
  YearlyPrice,
} from 'api/billing/types';

const capitalizeFirstLetter = (word: string): string =>
  `${word[0].toUpperCase()}${word.slice(1)}`;

export const getBillingVersion = (planId: PlanIds): BillingVersion => {
  return inRange(planId, 11, 24) ? BillingVersion.v2 : BillingVersion.v1;
};

export const getSupportedPlanName = (planName: PlanNames): SupportedPlans =>
  SupportedPlans[planName.toString().split('_')[0]];

export const getPlanName = (planName: PlanNames): Plans =>
  getSupportedPlanName(planName) ||
  RestrictedPlans[planName.toString().split('_')[0]];

export const getCurrentBillingCycle = (planName: PlanNames): BillingCycle =>
  BillingCycle[planName.toString().split('_')[1]];

export const getYearlySavings = (
  planName: SupportedPlans,
  quantity: number,
): number => {
  const plan = SupportedPlans[planName];

  return MonthlyPrice[plan] * quantity * 12 - YearlyPrice[plan] * quantity * 12;
};

export const getNextPlan = (currentPlan: Plan): Plans | undefined => {
  const version = getBillingVersion(currentPlan.planId);
  const planName = getPlanName(currentPlan.planName);
  const _planHierarchy = PlanHierarchy[version];

  return SupportedPlans[_planHierarchy[_planHierarchy[planName] + 1]];
};

/**
 * toLocaleString adds comma separator by en-US locale by default.
 */
export const formatAmount = (amount: number): string =>
  `${Math.round(amount / 100).toLocaleString()}`;

export const formatInvoiceAmount = (
  invoice: Invoice | InvoiceDetails,
): string => `${formatAmount(invoice.invoiceTotal)} ${invoice.currencyCode}`;

export const formatEstimateAmount = (
  estimate: ChangePlanEstimateResponse,
): string => `${formatAmount(estimate.invoiceTotal)} ${estimate.currencyCode}`;

export const getPlanPrice = (planName: PlanNames) => {
  const plan = getPlanName(planName);
  const billingCycle = getCurrentBillingCycle(planName);

  return billingCycle === BillingCycle.Monthly
    ? MonthlyPrice[plan]
    : YearlyPrice[plan];
};

export const getPlanId = (plan: Plans, billingCycle: BillingCycle): PlanIds =>
  PlanIds[
    `${capitalizeFirstLetter(plan)}_${capitalizeFirstLetter(billingCycle)}`
  ];

export const getPlanNameWithCycle = (
  plan: Plans,
  billingCycle: BillingCycle,
): PlanNames => PlanNames[PlanIds[getPlanId(plan, billingCycle)]];
